// Turn a {@link Model} into a live three.js scene graph that can play its
// animations. This is the one place that bridges the pure data (parts,
// variants, animations) to three.js, and it is shared by every renderer (the
// viewer today; a headless preview tomorrow). It keeps *animated* parts as
// their own nodes and bakes everything else into a single static mesh, so a
// still model stays one draw call while a windmill's sails can still turn.

import * as THREE from "three";
import type { Animation, ColorRepresentation, Interaction, Model, Part, Variant, Vec3 } from "./types";
import { buildModel } from "./build-model";
import { applySlots } from "./palette";
import { animatedTargets, sampleAnimation, sampleInteraction, type NodeTransform } from "./animate";
import { ACTOR_ROOT } from "./rig";

export interface InstanceOptions {
  /** Material for every mesh (default: unlit vertex-colour, matching the baked
   *  shading). The viewer passes a MeshLambert so the model casts shadows. */
  material?: THREE.Material;
  /** Recolour-slot overrides (e.g. `{ team: "#e74c3c" }`) — recolours every part
   *  tagged with that slot. See {@link applySlots} / {@link slotsOf}. */
  slots?: Record<string, ColorRepresentation>;
}

interface AnimNode {
  name: string;
  pivot: Vec3;
  group: THREE.Group;
}

/** A live, animatable model instance — add `.object` to a scene and call
 *  `.update()` each frame. Rebuild geometry with `setVariant`, pick a playing
 *  animation with `setAnimation`, free GPU memory with `dispose`. */
export class ModelInstance {
  readonly object = new THREE.Group();
  /** Local-space bounds of the current variant's full geometry. */
  readonly bounds = new THREE.Box3();
  private readonly material: THREE.Material;
  private readonly nodes: AnimNode[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private currentAnim: Animation | undefined;
  private currentVariantId: string;
  private slots: Record<string, ColorRepresentation> | undefined;

  constructor(private readonly model: Model, opts: InstanceOptions = {}) {
    this.material = opts.material ?? new THREE.MeshBasicMaterial({ vertexColors: true });
    this.slots = opts.slots;
    this.currentVariantId = model.defaultVariantId;
    this.setVariant(model.defaultVariantId);
  }

  /** Recolour the model's slots (e.g. a team colour) and rebuild the current
   *  variant. Pass `undefined` to restore each slot's authored default. */
  setSlots(slots: Record<string, ColorRepresentation> | undefined): void {
    this.slots = slots;
    this.setVariant(this.currentVariantId);
  }

  /** Rebuild the graph for a variant (by id). Resets any playing animation. */
  setVariant(variantId: string): void {
    const variant = this.model.variants.find((v) => v.id === variantId) ?? this.model.variants[0]!;
    this.currentVariantId = variant.id;
    this.clear();
    const parts = applySlots(variant.parts(), this.slots);
    const targets = animatedTargets(this.model.animations);

    // Split parts into the static base mesh and one mesh per animated name.
    const staticParts: Part[] = [];
    const byName = new Map<string, Part[]>();
    for (const p of parts) {
      if (p.name && targets.has(p.name)) {
        (byName.get(p.name) ?? byName.set(p.name, []).get(p.name)!).push(p);
      } else {
        staticParts.push(p);
      }
    }

    if (staticParts.length) {
      const geo = buildModel(staticParts);
      this.geometries.push(geo);
      const mesh = new THREE.Mesh(geo, this.material);
      mesh.castShadow = mesh.receiveShadow = true;
      this.object.add(mesh);
    }

    for (const [name, group] of byName) {
      const pivot = this.pivotFor(name);
      const geo = buildModel(group, [-pivot[0], -pivot[1], -pivot[2]]);
      this.geometries.push(geo);
      const mesh = new THREE.Mesh(geo, this.material);
      mesh.castShadow = mesh.receiveShadow = true;
      const node = new THREE.Group();
      node.position.set(pivot[0], pivot[1], pivot[2]);
      node.add(mesh);
      this.object.add(node);
      this.nodes.push({ name, pivot, group: node });
    }

    this.recomputeBounds(parts);
  }

  /** Choose which animation plays (by id), or `null`/unknown id to stop. */
  setAnimation(animId: string | null): void {
    this.currentAnim = animId ? this.model.animations.find((a) => a.id === animId) : undefined;
    this.applyRest();
  }

  /** Advance animation to wall-clock time `t` (seconds since it started). */
  update(t: number): void {
    if (!this.currentAnim) return;
    const sampled = sampleAnimation(this.currentAnim, t);
    for (const node of this.nodes) {
      const tf = sampled.get(node.name);
      if (tf) this.applyTransform(node, tf);
      else this.applyNodeRest(node);
    }
  }

  /** The animations playable on the current variant (respecting restrictions). */
  animationsForVariant(variantId: string): Animation[] {
    return this.model.animations.filter((a) => !a.variants || a.variants.includes(variantId));
  }

  dispose(): void {
    this.clear();
    if (this.material instanceof THREE.Material) this.material.dispose();
  }

  // ── internals ──────────────────────────────────────────────────────────────
  private applyTransform(node: AnimNode, tf: NodeTransform): void {
    node.group.position.set(node.pivot[0] + tf.position[0], node.pivot[1] + tf.position[1], node.pivot[2] + tf.position[2]);
    node.group.rotation.set(tf.rotation[0], tf.rotation[1], tf.rotation[2]);
    node.group.scale.set(tf.scale[0], tf.scale[1], tf.scale[2]);
  }

  private applyNodeRest(node: AnimNode): void {
    node.group.position.set(node.pivot[0], node.pivot[1], node.pivot[2]);
    node.group.rotation.set(0, 0, 0);
    node.group.scale.set(1, 1, 1);
  }

  private applyRest(): void {
    for (const node of this.nodes) this.applyNodeRest(node);
  }

  // The pivot for a name = the first track (across all animations) that targets
  // it and declares a pivot; the origin otherwise. A part name should pivot
  // consistently across the animations that drive it.
  private pivotFor(name: string): Vec3 {
    for (const a of this.model.animations) {
      for (const tr of a.tracks) if (tr.target === name && tr.pivot) return tr.pivot;
    }
    return [0, 0, 0];
  }

  private recomputeBounds(parts: Part[]): void {
    const geo = buildModel(parts);
    geo.computeBoundingBox();
    this.bounds.copy(geo.boundingBox ?? new THREE.Box3());
    geo.dispose();
  }

  private clear(): void {
    for (const child of [...this.object.children]) this.object.remove(child);
    for (const g of this.geometries) g.dispose();
    this.geometries.length = 0;
    this.nodes.length = 0;
  }
}

/** Convenience: build a {@link ModelInstance} for `model`. */
export function instantiateModel(model: Model, opts?: InstanceOptions): ModelInstance {
  return new ModelInstance(model, opts);
}

// ── Interactions — a live two-body graph (the model + an actor) ───────────────
// An interaction couples the model to an external actor posed at a station, so
// playing it needs *two* models in one graph: the model itself (its self tracks
// driving named parts) and an actor placed at the station (the "root" track
// moving the whole actor, other rig tracks moving a limb). This is the live
// counterpart of `bakeInteraction`: same motion, but kept as scene nodes so it
// can animate per-frame instead of rebuilding geometry each tick.

interface PosedNode { name: string; pivot: Vec3; group: THREE.Group; }

/** The pivot each track of `subject` declares for its target (origin if none). */
function pivotsFor(it: Interaction, subject: "self" | "actor"): Map<string, Vec3> {
  const m = new Map<string, Vec3>();
  for (const tr of it.tracks) {
    if ((tr.subject ?? "self") === subject && !m.has(tr.target)) m.set(tr.target, tr.pivot ?? [0, 0, 0]);
  }
  return m;
}

/** Build one side (model or actor) into `parent`: untracked parts merged into a
 *  static mesh, each tracked name kept as its own pivot node. */
function buildSide(
  parent: THREE.Group, parts: Part[], pivots: Map<string, Vec3>,
  material: THREE.Material, geometries: THREE.BufferGeometry[],
): PosedNode[] {
  const staticParts: Part[] = [];
  const byName = new Map<string, Part[]>();
  for (const p of parts) {
    if (p.name && pivots.has(p.name)) (byName.get(p.name) ?? byName.set(p.name, []).get(p.name)!).push(p);
    else staticParts.push(p);
  }
  if (staticParts.length) {
    const geo = buildModel(staticParts);
    geometries.push(geo);
    const mesh = new THREE.Mesh(geo, material);
    mesh.castShadow = mesh.receiveShadow = true;
    parent.add(mesh);
  }
  const nodes: PosedNode[] = [];
  for (const [name, group] of byName) {
    const pivot = pivots.get(name)!;
    const geo = buildModel(group, [-pivot[0], -pivot[1], -pivot[2]]);
    geometries.push(geo);
    const mesh = new THREE.Mesh(geo, material);
    mesh.castShadow = mesh.receiveShadow = true;
    const node = new THREE.Group();
    node.position.set(pivot[0], pivot[1], pivot[2]);
    node.add(mesh);
    parent.add(node);
    nodes.push({ name, pivot, group: node });
  }
  return nodes;
}

function applyPosed(node: PosedNode, tf: NodeTransform | undefined): void {
  const p = node.pivot;
  if (!tf) { node.group.position.set(p[0], p[1], p[2]); node.group.rotation.set(0, 0, 0); node.group.scale.set(1, 1, 1); return; }
  node.group.position.set(p[0] + tf.position[0], p[1] + tf.position[1], p[2] + tf.position[2]);
  node.group.rotation.set(tf.rotation[0], tf.rotation[1], tf.rotation[2]);
  node.group.scale.set(tf.scale[0], tf.scale[1], tf.scale[2]);
}

export interface InteractionOptions {
  /** Material for every mesh (default: unlit vertex-colour). */
  material?: THREE.Material;
  /** Which variant of the model to show (default: its default variant). */
  variantId?: string;
  /** Which variant of the actor to use (default: the actor's default variant). */
  actorVariantId?: string;
  /** Recolour-slot overrides applied to both the model and the actor (e.g. a
   *  team colour on a team-coloured actor). See {@link applySlots}. */
  slots?: Record<string, ColorRepresentation>;
}

/** A live interaction: the model plus an `actor` posed at the station. Add
 *  `.object` to a scene and call `.update(t)` each frame. */
export class InteractionInstance {
  readonly object = new THREE.Group();
  /** Local-space bounds of the model + actor at rest (for framing). */
  readonly bounds = new THREE.Box3();
  private readonly material: THREE.Material;
  private readonly ownMaterial: boolean;
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly selfNodes: PosedNode[];
  private readonly actorNodes: PosedNode[];
  private readonly rootGroup: THREE.Group;
  private readonly rootPivot: Vec3;

  constructor(model: Model, actor: Model, private readonly interaction: Interaction, opts: InteractionOptions = {}) {
    this.ownMaterial = !opts.material;
    this.material = opts.material ?? new THREE.MeshBasicMaterial({ vertexColors: true });

    const station = model.stations.find((s) => s.id === interaction.station);
    if (!station) throw new Error(`Interaction "${interaction.id}" references unknown station "${interaction.station}".`);

    // The model itself, its self tracks driving its named parts.
    const variant = pickVariant(model, opts.variantId);
    this.selfNodes = buildSide(this.object, applySlots(variant.parts(), opts.slots), pivotsFor(interaction, "self"), this.material, this.geometries);

    // The actor, placed at the station. A station group carries the placement; a
    // root group (with a -pivot content group under it) lets the "root" track
    // rotate/scale the whole actor about its pivot without disturbing the limbs.
    const stationGroup = new THREE.Group();
    stationGroup.position.set(station.position[0], station.position[1], station.position[2]);
    stationGroup.rotation.y = station.facing ?? 0;
    this.object.add(stationGroup);

    this.rootPivot = pivotsFor(interaction, "actor").get(ACTOR_ROOT) ?? [0, 0, 0];
    this.rootGroup = new THREE.Group();
    this.rootGroup.position.set(this.rootPivot[0], this.rootPivot[1], this.rootPivot[2]);
    stationGroup.add(this.rootGroup);
    const content = new THREE.Group();
    content.position.set(-this.rootPivot[0], -this.rootPivot[1], -this.rootPivot[2]);
    this.rootGroup.add(content);

    const actorPivots = pivotsFor(interaction, "actor");
    actorPivots.delete(ACTOR_ROOT);
    const actorVariant = pickVariant(actor, opts.actorVariantId);
    this.actorNodes = buildSide(content, applySlots(actorVariant.parts(), opts.slots), actorPivots, this.material, this.geometries);

    this.update(0);
    this.object.updateMatrixWorld(true);
    this.bounds.setFromObject(this.object);
  }

  /** Advance the interaction to wall-clock time `t` (seconds since it started). */
  update(t: number): void {
    const { self, actor } = sampleInteraction(this.interaction, t);
    for (const n of this.selfNodes) applyPosed(n, self.get(n.name));
    for (const n of this.actorNodes) applyPosed(n, actor.get(n.name));
    const tf = actor.get(ACTOR_ROOT);
    const p = this.rootPivot;
    if (tf) {
      this.rootGroup.position.set(p[0] + tf.position[0], p[1] + tf.position[1], p[2] + tf.position[2]);
      this.rootGroup.rotation.set(tf.rotation[0], tf.rotation[1], tf.rotation[2]);
      this.rootGroup.scale.set(tf.scale[0], tf.scale[1], tf.scale[2]);
    } else {
      this.rootGroup.position.set(p[0], p[1], p[2]);
      this.rootGroup.rotation.set(0, 0, 0);
      this.rootGroup.scale.set(1, 1, 1);
    }
  }

  dispose(): void {
    // Tear the graph down as well as freeing its buffers, mirroring
    // ModelInstance.dispose, so a disposed interaction leaves nothing behind.
    this.object.clear();
    for (const g of this.geometries) g.dispose();
    this.geometries.length = 0;
    if (this.ownMaterial && this.material instanceof THREE.Material) this.material.dispose();
  }
}

/** Resolve a variant by id, falling back to the model's default then the first. */
function pickVariant(model: Model, variantId: string | undefined): Variant {
  return (
    (variantId ? model.variants.find((v) => v.id === variantId) : undefined) ??
    model.variants.find((v) => v.id === model.defaultVariantId) ??
    model.variants[0]!
  );
}

/** Convenience: build an {@link InteractionInstance} for `model` + `actor`. */
export function instantiateInteraction(
  model: Model, actor: Model, interaction: Interaction, opts?: InteractionOptions,
): InteractionInstance {
  return new InteractionInstance(model, actor, interaction, opts);
}

/** Variant lookup helper used by the viewer and tests. */
export function variantById(model: Model, id: string): Variant | undefined {
  return model.variants.find((v) => v.id === id);
}
